import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { Store } from '@ngrx/store';
import * as moment from 'moment';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { CommonService } from '../../../../../../../../../../shared/services/common.service';
import { takeUntil } from 'rxjs/operators';
import { RecordExternalTestLabTestDetailsRequest } from '../../../../../../../../core/projects.actions';
import { ProjectsService } from '../../../../../../../../core/projects.service';

@Component({
    selector: 'app-external-test-record-lab-test-details',
    templateUrl: './external-test-record-lab-test-details.component.html',
    styleUrls: ['./external-test-record-lab-test-details.component.scss'],
    standalone: false
})
export class ExternalTestRecordLabTestDetailsComponent implements OnInit, OnDestroy {
  @Input() data: any;
  breakDuration: string;
  mediaFiles = [];
  errorMessages: any = {};
  errorMessageMap = {
    time: { required: 'This field is required.' },
    fromTime: { required: 'This field is required.' },
    fromTimePeriod: { required: 'This field is required.' },
    toTime: { required: 'This field is required.' },
    toTimePeriod: { required: 'This field is required.' },
    break: { required: 'This field is required.' },
    comment: { required: 'This field is required.' },
    pictures: { required: 'This field is required.' },
    videos: { required: 'This field is required.' },
  };
  form = new FormGroup<any>({
    time: new FormControl({ value: null, disabled: false }, [Validators.required]),
    fromTime: new FormControl({ value: null, disabled: false }, [Validators.required]),
    toTime: new FormControl({ value: null, disabled: false }, [Validators.required]),
    fromTimePeriod: new FormControl({ value: null, disabled: false }, [Validators.required]),
    toTimePeriod: new FormControl({ value: null, disabled: false }, [Validators.required]),
    break: new FormControl({ value: null, disabled: false }, [Validators.required]),
    comment: new FormControl({ value: null, disabled: false }),
    pictures: new FormControl({ value: JSON.stringify([]), disabled: false }),
    videos: new FormControl({ value: JSON.stringify([]), disabled: false }),
  });
  videoToBeDeletedFromMuxDb: any;
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private store: Store,
    private commonService: CommonService,
    private projectsService: ProjectsService,
  ) {
  }

  ngOnInit() {

    this.commonService.uploadedFilesInfo
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        const items = Array.isArray(data) ? data : [data];

        items.forEach((eachItem: any) => {
          if (eachItem?.['fileType'] === 'picture' || eachItem?.['fileType'] === 'document') {
            let pictures = this.form.get('pictures')?.value || '[]';
            pictures = JSON.parse(pictures);
            pictures.push(eachItem);
            this.form?.get('pictures')?.setValue(JSON.stringify(pictures));
            this.mediaFiles.push(eachItem);  // Add to mediaFiles array

          } else if (eachItem?.['fileType'] === 'video') {
            let videos = this.form.get('videos')?.value || '[]';
            videos = JSON.parse(videos);

            videos.push(eachItem);
            this.form?.get('videos')?.setValue(JSON.stringify(videos));
            this.mediaFiles.push(eachItem);  // Add to mediaFiles array
          }
        });

        this.mediaFiles = [...this.mediaFiles];
      });

    this.projectsService.changeView
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data) => {
        this.data = {};
        if (data?.id) {
          this.data = data;
          if (this.data?.inspection_detail?.break_duration) {
            this.breakDuration = this.getMinutesFromISO(this.data?.inspection_detail?.break_duration);
          }

          if (this.data?.inspection_detail?.pictures?.length) {
            this.mediaFiles = this.mapFileTypes(this.data.inspection_detail.pictures);
          }
        }
      });

    if (this.data?.inspection_detail?.break_duration) {
      this.breakDuration = this.getMinutesFromISO(this.data?.inspection_detail?.break_duration);
    }

    if (this.data?.inspection_detail?.pictures?.length) {
      this.mediaFiles = this.mapFileTypes(this.data.inspection_detail.pictures);
    }
  }

  save() {
    if (this.form.invalid) {
      this.checkForErrors();
      return;
    }

    const formValues = this.form.getRawValue();

    const arrival = this.buildVisitDateTime(formValues?.fromTime, formValues.fromTimePeriod);
    const departure = this.buildVisitDateTime(formValues?.toTime, formValues.toTimePeriod);
    // A departure that is not after the arrival means the visit crossed midnight.
    if (departure.isSameOrBefore(arrival)) {
      departure.add(1, 'day');
    }

    const payload = {
      comment: formValues?.comment,
      pictures: JSON.parse(formValues?.pictures)?.map(o => o.id),
      videos: JSON.parse(formValues?.videos)?.map(o => o.id),
      arrival_time: arrival.toISOString(),
      departure_time: departure.toISOString(),
      break_duration: this.getISOFromMinutes(formValues?.break),
      testId: this.data.id,
    };

    this.store.dispatch(RecordExternalTestLabTestDetailsRequest({ payload }));
  }

  checkForErrors(currentField?: string) {
    this.errorMessages = {
      ...this.errorMessages,
      ...(this.commonService.checkFormValidation(this.form, this.errorMessageMap, currentField)),
    };
  }

  /**
   * Builds a visit datetime by anchoring the entered time-of-day to the test's
   * scheduled date (request_date). The UI only captures a time; the date is
   * always implied to be the schedule date.
   *
   * Replaces the previous convertTime(), which hardcoded America/Los_Angeles and
   * double-converted UTC. On a Central-time project that netted a ~12h shift,
   * flipping AM/PM and re-anchoring the date to "today" (the recording day) —
   * e.g. an entered 8:00 AM-4:00 PM day shift was stored as 8:00 PM (prev day) to
   * 4:00 AM, breaking the lab "Visited on" email.
   */
  buildVisitDateTime(time: string, period: string): moment.Moment {
    // tslint:disable-next-line:prefer-const
    let [hours, minutes = 0] = (time || '').split(':').map(Number);

    if (period === 'PM' && hours < 12) {
      hours += 12;
    }
    if (period === 'AM' && hours === 12) {
      hours = 0;
    }

    return moment(this.data?.request_date)
      .set({ hour: hours, minute: minutes, second: 0, millisecond: 0 });
  }

  getMinutesFromISO(durationStr) {
    const duration = moment.duration(durationStr);
    return duration.asMinutes().toString();
  }

  getISOFromMinutes(minutes: number | string): string {
    const minsNum = Number(minutes);

    if (isNaN(minsNum) || minsNum <= 0) {
      return 'PT0M';
    }

    const hrs = Math.floor(minsNum / 60);
    const mins = minsNum % 60;

    let iso = 'PT';
    if (hrs > 0) {
      iso += `${hrs}H`;
    }
    if (mins > 0) {
      iso += `${mins}M`;
    }

    return iso;
  }

  deletePicture(pic) {
    if (pic.fileType === 'picture' || pic.fileType === 'document') {
      let pictures = this.getPictures();

      pictures = pictures?.filter(o => o.id !== pic.id);
      this.mediaFiles = this.mediaFiles?.filter(o => o.id !== pic.id);
      this.form?.get('pictures')?.setValue(JSON.stringify(pictures));
    }

    if (pic.fileType === 'video') {
      let videos = this.getVideos();

      videos = videos?.filter(o => o.id !== pic.id);
      this.mediaFiles = this.mediaFiles?.filter(o => o.id !== pic.id);
      this.form?.get('videos')?.setValue(JSON.stringify(videos));
      this.videoToBeDeletedFromMuxDb.push(pic.id);
    }
  }

  getPictures() {
    const pictures = this.form.controls['pictures']?.value || '[]';
    return JSON.parse(pictures);
  }

  getVideos() {
    const videos = this.form.controls['videos']?.value || '[]';
    return JSON.parse(videos);
  }

  private mapFileTypes(attachments: any[]): any[] {
    return JSON.parse(JSON.stringify(attachments)).map(item => {
      if (!item.fileType) {
        if (item.mime?.startsWith('image/')) {
          item.fileType = 'picture';
        } else if (item.mime?.startsWith('video/')) {
          item.fileType = 'video';
        } else if (item.mime?.startsWith('application/') || item.mime?.startsWith('text/')) {
          item.fileType = 'document';
        }
      }
      return item;
    });
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
